import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  guardianRelationships,
  type ChildRequestView,
  type GuardianRelationship,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import {
  childRequestBody,
  childRequestStatusLabelKeys,
  emptyChildRequestForm,
  type ChildRequestForm,
} from './childRequests';
import { guardianRelationshipKeys } from './guardianLinks';

export interface ChildRequestSectionProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

/**
 * For a guardian: name a child to follow, and see what happened to each request. A request shows
 * nothing about the club's athletes; the child appears under My children once a secretary approves.
 */
export function ChildRequestSection({
  locale,
  organizationId,
  csrfToken,
}: ChildRequestSectionProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/child-requests`;
  const [requests, setRequests] = useState<ChildRequestView[] | null>(null);
  const [form, setForm] = useState<ChildRequestForm>(emptyChildRequestForm);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [sent, setSent] = useState(false);

  const load = useCallback(async () => {
    try {
      setRequests(await apiRequest<ChildRequestView[]>(`${base}/mine`));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const body = childRequestBody(form);

  async function submit(): Promise<void> {
    if (body === null) return;
    setErrorKey(null);
    setSent(false);
    try {
      await apiRequest<ChildRequestView>(base, { method: 'POST', csrfToken, body });
      setForm(emptyChildRequestForm);
      setSent(true);
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function withdraw(request: ChildRequestView): Promise<void> {
    setErrorKey(null);
    setSent(false);
    try {
      await apiRequest<ChildRequestView>(`${base}/${request.id}/withdraw`, {
        method: 'POST',
        csrfToken,
        ifMatchVersion: request.version,
        body: {},
      });
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
    await load();
  }

  return (
    <Box component="section" aria-labelledby="child-requests-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="child-requests-title" gutterBottom>
        {translate(locale, 'childRequests.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'childRequests.hint')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {sent && errorKey === null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'childRequests.sent')}
        </Alert>
      )}
      <Stack
        component="form"
        spacing={2}
        sx={{ mb: 3 }}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            required
            size="small"
            label={translate(locale, 'athletes.givenName')}
            value={form.givenName}
            onChange={(event) => {
              setForm({ ...form, givenName: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            required
            size="small"
            label={translate(locale, 'athletes.familyName')}
            value={form.familyName}
            onChange={(event) => {
              setForm({ ...form, familyName: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            required
            size="small"
            type="date"
            label={translate(locale, 'athletes.dateOfBirth')}
            value={form.dateOfBirth}
            onChange={(event) => {
              setForm({ ...form, dateOfBirth: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'guardianLinks.relationship')}
            value={form.relationship}
            onChange={(event) => {
              setForm({ ...form, relationship: event.target.value as GuardianRelationship });
            }}
            sx={{ minWidth: 200 }}
          >
            {guardianRelationships.map((relationship) => (
              <MenuItem key={relationship} value={relationship}>
                {translate(locale, guardianRelationshipKeys[relationship])}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          size="small"
          multiline
          minRows={2}
          label={translate(locale, 'childRequests.note')}
          value={form.note}
          onChange={(event) => {
            setForm({ ...form, note: event.target.value });
          }}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        <Box>
          <Button type="submit" variant="contained" disabled={body === null}>
            {translate(locale, 'childRequests.submit')}
          </Button>
        </Box>
      </Stack>
      <Typography variant="subtitle1" component="h3" gutterBottom>
        {translate(locale, 'childRequests.mine')}
      </Typography>
      {requests === null && errorKey === null && (
        <Typography>{translate(locale, 'childRequests.loading')}</Typography>
      )}
      {requests?.length === 0 && (
        <Typography>{translate(locale, 'childRequests.empty')}</Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {requests?.map((request) => (
          <Box component="li" key={request.id} sx={{ borderTop: 1, borderColor: 'divider', py: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ flexGrow: 1 }}>
                {athleteDisplayName(request, locale)} ·{' '}
                {formatCalendarDate(request.dateOfBirth, locale)} ·{' '}
                {translate(locale, guardianRelationshipKeys[request.relationship])}
              </Typography>
              <Chip
                size="small"
                label={translate(locale, childRequestStatusLabelKeys[request.status])}
                color={
                  request.status === 'approved'
                    ? 'success'
                    : request.status === 'pending'
                      ? 'warning'
                      : 'default'
                }
              />
              {request.status === 'pending' && (
                <Button
                  size="small"
                  onClick={() => {
                    void withdraw(request);
                  }}
                >
                  {translate(locale, 'childRequests.withdraw')}
                </Button>
              )}
            </Box>
            {request.decisionReason !== null && (
              <Typography variant="body2" color="text.secondary">
                {translate(locale, 'childRequests.reason')}: {request.decisionReason}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
